import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { COMPANIES, ROUTE_PATHS, companyPath } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-28 min-h-[60vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1532328076801-a862c9d5a74a?w=1600&q=85"
            alt="About ALV"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <p className="eyebrow mb-5">About ALV</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-3xl">
              A Legacy Built on{' '}
              <span className="text-gold italic">Excellence</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <FadeUp>
              <p className="eyebrow mb-4">Our Mission</p>
              <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5] mb-6">
                Redefining What a Pageant Organization Can Be
              </h2>
              <div className="divider-gold-left" />
              <div className="space-y-5 mt-6 text-[#f5f5f5]/60 leading-relaxed text-[0.95rem]">
                <p>
                  ALV Pageant Circle was founded on a single conviction: that extraordinary talent deserves an extraordinary system. For over a decade, we have built, refined, and elevated a vertically integrated ecosystem that covers every dimension of a talent's journey.
                </p>
                <p>
                  Unlike traditional pageant organizations, ALV operates as a full-spectrum ecosystem. We discover, train, manage, commercialize, and expose talent — all under one unified brand architecture that ensures consistency, quality, and global competitiveness.
                </p>
                <p>
                  Today, ALV Pageant Circle stands as the most comprehensive talent organization in the Philippines — with representation across talent management, events production, advertising, and international pageant competition.
                </p>
              </div>
              <Link to={ROUTE_PATHS.ECOSYSTEM} className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-300">
                Explore Our Ecosystem <ArrowRight size={12} />
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="relative">
                <div className="card-editorial h-[520px]">
                  <img
                    src="https://images.unsplash.com/photo-1673278206746-7d874c2eb545?w=900&q=85"
                    alt="ALV Mission"
                    className="w-full h-full object-cover"
                  />
                  <div className="card-overlay" />
                </div>
                {/* Floating stat */}
                <div className="absolute -bottom-6 -left-6 bg-[#0e0e0e] border border-[rgba(201,168,76,0.2)] p-6 shadow-xl">
                  <p className="heading-display text-4xl text-gold mb-1">12+</p>
                  <p className="eyebrow text-[9px] opacity-60">Years of Excellence</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-16">
            <p className="eyebrow mb-4">Our Philosophy</p>
            <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5]">
              The Principles that Drive Us
            </h2>
            <div className="divider-gold mt-5" />
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(201,168,76,0.08)]">
            {[
              {
                num: '01',
                title: 'Vertical Integration',
                desc: 'Every stage of a talent\'s journey — from discovery to world-stage — is handled within the ALV ecosystem. No gaps, no compromises.',
              },
              {
                num: '02',
                title: 'Global Standard',
                desc: 'We measure ourselves against the best in the world — Miss Universe, Miss World, and the world\'s foremost luxury talent agencies.',
              },
              {
                num: '03',
                title: 'Purpose-Driven',
                desc: 'Behind every crown is a cause. ALV champions talent that uses its platform to drive meaningful change in the world.',
              },
            ].map((value, i) => (
              <FadeUp key={i} delay={i * 0.15}>
                <div className="bg-[#090909] p-10 lg:p-14 h-full">
                  <span className="heading-display text-5xl text-gold opacity-15 block mb-4">{value.num}</span>
                  <h3 className="heading-display text-2xl text-[#f5f5f5] mb-4">{value.title}</h3>
                  <div className="divider-gold-left" />
                  <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mt-5">{value.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Companies teaser */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-16">
            <p className="eyebrow mb-4">Our Family</p>
            <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5]">
              Five Companies, One Vision
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {COMPANIES.map((company, i) => (
              <FadeUp key={company.id} delay={i * 0.08}>
                <Link to={companyPath(company.slug)} className="block group">
                  <div className="card-editorial h-64">
                    <img src={company.heroImage} alt={company.name} loading="lazy" />
                    <div className="card-overlay" />
                    <div className="absolute inset-0 flex flex-col justify-end p-5">
                      <span
                        className="text-[9px] font-bold tracking-widest border border-[rgba(201,168,76,0.4)] w-8 h-8 flex items-center justify-center text-[#c9a84c] mb-3"
                      >
                        {company.logoInitials}
                      </span>
                      <h4 className="heading-display text-base text-[#f5f5f5] leading-snug">{company.name}</h4>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
